// #region EXECUTION columns definitions
import { Action } from '@ngrx/store';
import { CreatorsNotAllowedCheck } from '@ngrx/store/src/models';
import { TranslateService } from '@ngx-translate/core';
import { IconType } from '@se/cp.common';
import { ExaminationWorklistDTO } from '@se/models';
import { seToolbarButtonPressed } from '../../se-toolbar/se-buttons-toolbar/redux/se-buttons-toolbar.actions';
import { ToolbarAction } from '../../se-toolbar/se-buttons-toolbar/types';
import { GridUtilitiesService } from '../../services/grid-utilities.service';
import { CommonSettingsService } from '../../services/settings/common-settings.service';
import { seSearchSelect } from '../redux/search-page.actions';
import { SearchFormType } from '../search-page-type.enum';
import { EstensaColDef } from '../search-param.model';
import { ITooltipParams } from 'ag-grid-community';

//le icone soggette a setting sono nascoste, gli altri sono commentati per non ritrovarli nel menu di configurazione
export function executionColumnDefs(
    translate: TranslateService,
    gridUtils: GridUtilitiesService,
    dispatchAction: <V extends Action>(action: V & CreatorsNotAllowedCheck<V>) => void,
    commonSettingService: CommonSettingsService,
): EstensaColDef<ExaminationWorklistDTO>[] {
    return [
        {
            colId: 'FeaAuthHead',
            headerName: translate.instant('FeaAuthHead'),
            headerTooltip: translate.instant('FeaAuthorization'),
            valueGetter: (e) => {
                return !!e.data?.feaData?.hasPatientAuthorization;
            },
            minWidth: 25,
            maxWidth: 50,
            cellStyle: { display: 'flex', justifyContent: 'center' },
            cellRenderer: 'boolIconRenderer',
            tooltipField: translate.instant('FeaAuthHead'),
            cellRendererParams: {
                estensa: {
                    iconType: IconType.SeFont,
                    icon: 'icon_fea_auth',
                    tooltip: translate.instant('FeaAuth'),
                },
            },
            cellClass: "icon-font-execution-theme",
            sortable: false,
            context: { estensaDetails: { primary: true } },
            hide: !commonSettingService.isFeaEnabled(),
        },
        {
            colId: 'FeaExamConsentHead',
            headerName: '',
            valueGetter: (e) => e.data,
            width: 26,
            minWidth: 26,
            cellStyle: { padding: '0', display: 'flex', justifyContent: 'center', width: '26px' },
            tooltipField: translate.instant('FeaExamConsentHead'),
            cellRenderer: 'boolIconRenderer',
            cellRendererParams: (data: ExaminationWorklistDTO) => {
                if (data.feaData != null && data.feaData.hasPatientAuthorization && !data.feaData.isConsentValidated) {
                    return {
                        estensa: {
                            iconType: IconType.AngularMaterial,
                            icon: 'check',
                            color: 'yellow',
                            tooltip: translate.instant('FeaExamConsentHead'),
                        },
                    };
                }
                if (data.feaData != null && data.feaData.hasPatientAuthorization && data.feaData.isConsentValidated) {
                    return {
                        estensa: {
                            iconType: IconType.AngularMaterial,
                            icon: 'check',
                            color: 'green',
                            tooltip: translate.instant('FeaExamConsentHead'),
                        },
                    };
                }
            },
            sortable: false,
            context: { estensaDetails: { primary: false } },
            hide: true,
        },
        {
            colId: 'SendToWorklist',
            headerName: '',
            valueGetter: (e) => !!e.data?.command.mustBeManualSentToWorklist,
            minWidth: 25,
            maxWidth: 50,
            cellStyle: { display: 'flex', justifyContent: 'center' },
            cellRenderer: 'buttonIconRenderer',
            cellRendererParams: {
                estensa: {
                    iconType: IconType.SeFont,
                    icon: 'icon_send_worklist',
                    tooltip: translate.instant('SendToWorklist'),
                    selectOnClick: true,
                    onClick: (id: string) => {
                        dispatchAction(seSearchSelect({ formType: SearchFormType.EXECUTION, items: [id], rows: [] }));
                        dispatchAction(seToolbarButtonPressed({ button: ToolbarAction.SEND_TO_WORKLIST }));
                    },
                },
            },
            cellClass: "icon-font-button-execution-theme",
            tooltipField: translate.instant('SendToWorklist'),
            sortable: false,
            context: { estensaDetails: { primary: true } },
            hide: false,
        },
        {
            colId: 'Doses',
            headerName: '',
            valueGetter: (e) => e.data?.doses,
            minWidth: 25,
            maxWidth: 50,
            cellStyle: { display: 'flex', justifyContent: 'center' },
            cellRenderer: 'boolIconRenderer',
            cellRendererParams: (data: any) => {
                if (data.data.doses && data.data.doses.length) {
                    return {
                        estensa: {
                            iconType: IconType.SeFont,
                            icon: 'icon_radiation_dose',
                        }
                    };
                }
            },
            cellClass: "icon-font-execution-theme",
            tooltipValueGetter: (p: ITooltipParams): string | undefined => {
                const dose = p?.data?.doses?.[0];
                if (!dose) return;

                return `${translate.instant('Description')}: ${dose.description}, ${translate.instant('Quantity')}: ${dose.quantity} ${dose.unit}`;
            },
            sortable: false,
            context: { estensaDetails: { primary: true } },
            hide: false,
        },
        {
            colId: 'ClinicalAlert',
            headerName: '',
            headerTooltip: translate.instant('ClinicalAlert'),
            headerComponent: 'boolIconRenderer',
            headerComponentParams: () => {
                return {
                    estensa: {
                        iconType: IconType.SeFont,
                        icon: 'icon_clinical_alert',
                        negate: true
                    }
                };
            },
            headerStyle: { display: 'flex', justifyContent: 'center' },
            headerClass: "icon-header-color-clinical-alert-theme",
            valueGetter: (e) => e.data?.clinicalAlert,
            minWidth: 25,
            maxWidth: 50,
            cellStyle: { display: 'flex', justifyContent: 'center' },
            cellRenderer: 'boolIconRenderer',
            cellRendererParams: (data: any) => {
                if (data.data.clinicalAlert) {
                    return {
                        estensa: {
                            iconType: IconType.SeFont,
                            icon: 'icon_clinical_alert'
                        }
                    };
                }
            },
            cellClass: "icon-color-clinical-alert-theme",
            tooltipValueGetter: (p: ITooltipParams): string | undefined => {
                const clinicalAlert = p.data.clinicalAlert;
                if (!clinicalAlert) return;
                return clinicalAlert;
            },
            sortable: false,
            context: { estensaDetails: { primary: true } },
            hide: false
        },
        {
            colId: 'AttachedDocuments',
            headerName: '',
            valueGetter: (e) => e.data?.hasAttachedDocuments || e.data?.hasAttachedDdevices,
            minWidth: 25,
            maxWidth: 50,
            suppressSizeToFit: true,
            resizable: false,
            sortable: false,
            cellStyle: { padding: '0', display: 'flex', justifyContent: 'center', width: '26px' },
            cellRenderer: 'boolIconRenderer',
            cellRendererParams: (params: any) => {
                return {
                    estensa: {
                        iconType: IconType.SeFont,
                        icon: 'icon_attached_documents',
                        tooltip: getTooltip(params.data, translate)
                    }
                };
            },
            cellClass: "icon-font-execution-theme",
            context: { estensaDetails: { primary: true } },
            hide: false,
        },
        {
            colId: '0',
            headerName: translate.instant('BookingDate'),
            headerTooltip: translate.instant('BookingDate'),
            field: 'plannedDate',
            width: 150,
            minWidth: 150,
            valueFormatter: gridUtils.dateTimeFormatter('plannedDate'),
            comparator: gridUtils.dateComparator,
            suppressSizeToFit: false,
            tooltipValueGetter: (p) => p.valueFormatted,
            context: { estensaDetails: { primary: false, valueFormatter: gridUtils.dateTimeFormatter('plannedDate') } },
            hide: false,
        },

        {
            colId: '1',
            headerName: translate.instant('AdmissionDate'),
            headerTooltip: translate.instant('AdmissionDate'),
            field: 'admissionDate',
            width: 150,
            minWidth: 150,
            sort: 'asc',
            valueFormatter: gridUtils.dateTimeFormatter('admissionDate'),
            comparator: gridUtils.dateComparator,
            suppressSizeToFit: false,
            tooltipValueGetter: (p) => p.valueFormatted,
            context: {
                estensaDetails: { primary: false, valueFormatter: gridUtils.dateTimeFormatter('admissionDate') },
            },
            hide: false,
        },
        {
            colId: '2',
            headerName: translate.instant('ExamDate'),
            headerTooltip: translate.instant('ExamDate'),
            tooltipValueGetter: (p) => p.valueFormatted,
            field: 'examinationDate',
            width: 150,
            minWidth: 150,
            valueFormatter: gridUtils.dateTimeFormatter('examinationDate'),
            comparator: gridUtils.dateComparator,
            context: {
                estensaDetails: { primary: true, valueFormatter: gridUtils.dateTimeFormatter('executionDate') },
            },
            hide: false,
        },
        {
            colId: '3',
            headerName: translate.instant('ExecutionDate'),
            headerTooltip: translate.instant('ExecutionDate'),
            tooltipValueGetter: (p) => p.valueFormatted,
            field: 'executionDate',
            width: 150,
            minWidth: 150,
            valueFormatter: gridUtils.dateTimeFormatter('executionDate'),
            comparator: gridUtils.dateComparator,
            context: {
                estensaDetails: { primary: false, valueFormatter: gridUtils.dateTimeFormatter('executionDate') },
            },
            hide: false,
        },
        {
            colId: '4',
            headerName: translate.instant('ExecutorTechnician'),
            headerTooltip: translate.instant('ExecutorTechnician'),
            tooltipValueGetter: (p) => p.valueFormatted,
            field: 'technician.fullName',
            width: 150,
            minWidth: 150,
            context: {
                estensaDetails: { primary: true },
            },
            hide: false,
        },
        {
            colId: '5',
            headerName: translate.instant('Patient'),
            headerTooltip: translate.instant('Patient'),
            valueGetter: (e) => {
                const lastName = e.data?.patient.surname ?? '';
                const firstName = e.data?.patient.name ?? '';
                return `${lastName.toLocaleUpperCase()} ${firstName.toLocaleUpperCase()}`;
            },
            tooltipValueGetter: (e) => {
                const lastName = e.data?.patient.surname ?? '';
                const firstName = e.data?.patient.name ?? '';
                return `${lastName.toLocaleUpperCase()} ${firstName.toLocaleUpperCase()}`;
            },
            width: 150,
            minWidth: 150,
            suppressSizeToFit: true,
            context: {
                estensaDetails: {
                    primary: true,
                    valueGetter: (data: ExaminationWorklistDTO) => {
                        const lastName = data.patient.surname ?? '';
                        const firstName = data.patient.name ?? '';
                        return `${lastName.toLocaleUpperCase()} ${firstName.toLocaleUpperCase()}`;
                    },
                },
            },
            hide: false,
        },
        {
            colId: '6',
            headerName: translate.instant('BirthDate'),
            headerTooltip: translate.instant('BirthDate'),
            tooltipValueGetter: (p) => p.valueFormatted,
            field: 'patient.birthDate',
            width: 150,
            minWidth: 150,
            valueFormatter: gridUtils.dateFormatter(['patient', 'birthDate']),
            comparator: gridUtils.dateComparator,
            context: {
                estensaDetails: { primary: true, valueFormatter: gridUtils.dateFormatter(['patient', 'birthDate']) },
            },
            hide: false,
        },
        {
            colId: '7',
            headerName: translate.instant('TaxCode'),
            headerTooltip: translate.instant('TaxCode'),
            tooltipField: 'patient.taxCode',
            field: 'patient.taxCode',
            width: 150,
            minWidth: 150,
            suppressSizeToFit: false,
            context: { estensaDetails: { primary: false } },
            hide: false,
        },
        {
            colId: '8',
            headerName: translate.instant('PatientIdentifier'),
            headerTooltip: translate.instant('PatientIdentifier'),
            tooltipField: 'patient.patientId',
            field: 'patient.patientId',
            width: 150,
            minWidth: 150,
            suppressSizeToFit: false,
            context: { estensaDetails: { primary: false } },

            hide: false,
        },
        {
            colId: '9',
            headerName: translate.instant('CARDIO_Identifier'),
            headerTooltip: translate.instant('CARDIO_Identifier'),
            tooltipField: undefined,
            field: undefined,
            width: 150,
            minWidth: 150,
            suppressSizeToFit: false,
            context: { estensaDetails: { primary: false } },

            hide: false,
        },
        {
            colId: '10',
            headerName: translate.instant('Exam'),
            comparator: (valueA: any, valueB: any) => {
                const a = valueA?.toString().toLowerCase() ?? '';
                const b = valueB?.toString().toLowerCase() ?? '';
                return a.localeCompare(b);
            },
            headerTooltip: translate.instant('Exam'),
            field: 'exam.description',
            width: 150,
            minWidth: 150,
            suppressSizeToFit: false,
            context: { estensaDetails: { primary: true } },

            hide: false,
        },
        {
            colId: '11',
            headerName: translate.instant('Status'),
            headerTooltip: translate.instant('Status'),
            tooltipField: 'status',
            field: 'status',
            width: 150,
            minWidth: 150,
            suppressSizeToFit: false,
            context: { estensaDetails: { primary: true } },

            hide: false,
        },
        {
            colId: '12',
            headerName: translate.instant('Severity'),
            headerTooltip: translate.instant('Severity'),
            tooltipField: 'severity.description',
            field: 'severity.description',
            width: 150,
            minWidth: 150,
            suppressSizeToFit: false,
            context: { estensaDetails: { primary: true } },

            hide: false,
        },
        {
            colId: '13',
            headerName: translate.instant('Provenance'),
            headerTooltip: translate.instant('Provenance'),
            tooltipField: 'provenance.description',
            field: 'provenance.description',
            width: 150,
            minWidth: 150,
            suppressSizeToFit: false,
            context: { estensaDetails: { primary: true } },

            hide: false,
        },
        {
            colId: '14',
            headerName: translate.instant('PatientState'),
            headerTooltip: translate.instant('PatientState'),
            tooltipField: 'patientStatus.description',
            field: 'patientStatus.description',
            width: 150,
            minWidth: 150,
            suppressSizeToFit: false,
            context: { estensaDetails: { primary: false } },

            hide: false,
        },

        {
            colId: '15',
            headerName: translate.instant('Equipment'),
            headerTooltip: translate.instant('Equipment'),
            tooltipField: 'equipment.description',
            field: 'equipment.description',
            width: 150,
            minWidth: 150,
            suppressSizeToFit: false,
            context: { estensaDetails: { primary: false } },

            hide: false,
        },

        {
            colId: '16',
            headerName: translate.instant('Room'),
            headerTooltip: translate.instant('Room'),
            tooltipField: 'room.description',
            field: 'room.description',
            width: 150,
            minWidth: 150,
            context: { estensaDetails: { primary: false } },

            hide: false,
        },
        {
            colId: '17',
            headerName: translate.instant('BusinessUnit'),
            headerTooltip: translate.instant('BusinessUnit'),
            tooltipField: 'businessUnit.description',
            field: 'businessUnit.description',
            width: 150,
            minWidth: 150,
            suppressSizeToFit: false,
            context: { estensaDetails: { primary: false } },

            hide: false,
        },

        {
            colId: 'TicketOkHead',
            headerName: '',
            headerTooltip: translate.instant('TicketOk'),
            valueGetter: (e) => e.data,
            cellStyle: { display: 'flex', justifyContent: 'center' },
            cellRenderer: 'stateIconRenderer',
            cellRendererParams: {
                estensa(data: ExaminationWorklistDTO) {
                    if (data.ticket === true) {
                        return {
                            iconType: IconType.AngularMaterial,
                            icon: 'check',
                            fontSize: '20px',
                            color: 'green',
                            tooltip: translate.instant('TicketOk'),
                        };
                    }
                    if (data.ticket === false) {
                        return {
                            iconType: IconType.AngularMaterial,
                            icon: 'check',
                            fontSize: '20px',
                            color: 'red',
                            tooltip: translate.instant('TicketNonEsente/NonPagato'),
                        };
                    }
                },
            },
            width: 150,
            minWidth: 150,
            context: { estensaDetails: { primary: false } },

            hide: false,
        },
        {
            colId: '18',
            headerName: translate.instant('Accession_Number'),
            headerTooltip: translate.instant('Accession_Number'),
            tooltipField: 'accessionNumber',
            field: 'accessionNumber',
            width: 150,
            minWidth: 150,
            context: { estensaDetails: { primary: false } },

            hide: false,
        },
        {
            colId: '19',
            headerName: translate.instant('AdmissionNumber'),
            headerTooltip: translate.instant('AdmissionNumber'),
            tooltipField: 'admissionNumber',
            field: 'admissionNumber',
            width: 150,
            minWidth: 150,
            context: { estensaDetails: { primary: false } },

            hide: true,
        },
        {
            colId: '20',
            headerName: translate.instant('ExternalAdmissionNumber'),
            headerTooltip: translate.instant('ExternalAdmissionNumber'),
            tooltipField: 'externalAdmissionNumber',
            field: 'externalAdmissionNumber',
            width: 150,
            minWidth: 150,
            context: { estensaDetails: { primary: false } },

            hide: false,
        },
        {
            colId: '21',
            headerName: translate.instant('FolderNumber'),
            headerTooltip: translate.instant('FolderNumber'),
            tooltipField: 'folderNumber',
            field: 'folderNumber',
            width: 150,
            minWidth: 150,
            context: { estensaDetails: { primary: false } },

            hide: false,
        },
        {
            colId: '22',
            headerName: translate.instant('HospitalizationNumberShort'),
            headerTooltip: translate.instant('HospitalizationNumberShort'),
            tooltipField: 'hospitalizationNumber',
            field: 'hospitalizationNumber',
            width: 150,
            minWidth: 150,
            context: { estensaDetails: { primary: false } },

            hide: false,
        },
        {
            colId: '23',
            headerName: translate.instant('ReportDate'),
            headerTooltip: translate.instant('ReportDate'),
            tooltipValueGetter: (p) => p.valueFormatted,
            tooltipField: 'reportDate',
            valueFormatter: gridUtils.dateTimeFormatter('reportDate'),
            comparator: gridUtils.dateComparator,
            field: 'reportDate',
            width: 150,
            minWidth: 150,
            suppressSizeToFit: false,
            context: { estensaDetails: { primary: false, valueFormatter: gridUtils.dateTimeFormatter('reportDate') } },

            hide: false,
        },
        {
            colId: '24',
            headerName: translate.instant('ReferringPhysician'),
            headerTooltip: translate.instant('ReferringPhysician'),
            tooltipField: 'referringPhysician.fullName',
            field: 'referringPhysician.fullName',
            width: 150,
            minWidth: 150,
            suppressSizeToFit: false,
            context: { estensaDetails: { primary: false } },

            hide: false,
        },
        {
            colId: '25',
            headerName: translate.instant('AiStatus'),
            headerTooltip: translate.instant('AiStatus'),
            tooltipField: 'aiData.status',
            field: 'aiData.status',
            width: 150,
            minWidth: 150,
            suppressSizeToFit: false,
            context: { estensaDetails: { primary: false } },
            hide: false,
        },
        {
            colId: '26',
            headerName: translate.instant('AiClinicalApp'),
            headerTooltip: translate.instant('AiClinicalApp'),
            tooltipField: 'aiData.clinicalApp',
            field: 'aiData.clinicalApp',
            width: 150,
            minWidth: 150,
            suppressSizeToFit: false,
            context: { estensaDetails: { primary: false } },
            hide: false,
        },
    ];
}
export function getTooltip(params: any, translate: TranslateService): string {
    if (params.hasAttachedDocuments && params.hasAttachedDdevices) {
        return translate.instant('DocumentsAndDevicesAttached');
    }
    else if (params.hasAttachedDocuments) {
        return translate.instant('AttachedDocuments');
    }
    else if (params.hasAttachedDdevices) {
        return translate.instant('AttachedDevices');
    } else {
        return '';
    }
}
export function primaryExecutionColumnDef(
    translate: TranslateService,
    gridUtils: GridUtilitiesService,
    dispatchAction: <V extends Action>(action: V & CreatorsNotAllowedCheck<V>) => void,
    commonSettingService: CommonSettingsService,
): EstensaColDef[] {
    let colDef = executionColumnDefs(translate, gridUtils, dispatchAction, commonSettingService);
    return colDef.filter((col) => col.context.estensaDetails?.primary === true);
}

export function secondaryExecutionColumnDef(
    translate: TranslateService,
    gridUtils: GridUtilitiesService,
    dispatchAction: <V extends Action>(action: V & CreatorsNotAllowedCheck<V>) => void,
    commonSettingService: CommonSettingsService,
): EstensaColDef[] {
    let colDef = executionColumnDefs(translate, gridUtils, dispatchAction, commonSettingService);
    return colDef.filter((col) => col.context.estensaDetails?.primary === false);
}

// primary che dipende dalla presenza del valore
//export function primaryByFieldValue(items: any[], allColumnsDef: EstensaColDef[], formType: SearchFormType) {
//    const showColDose = items.some((el) => el.hasDose);
//    const showColSendToWorklist = items.some((el) => el.command.mustBeManualSentToWorklist);
//    const showColAiScore = items.some((el) => el.aiData.scoreValue);
//    allColumnsDef.map((col: EstensaColDef) => {
//        if (col.colId === 'Doses') {
//            showColDose ? col.estensaDetails.primary = false : col.estensaDetails.primary = true;
//        }
//        if (col.colId === 'SendToWorklist') {
//            showColSendToWorklist ? col.estensaDetails.primary = true : col.estensaDetails.primary = false;
//        }
//        if (col.colId === 'AiScore') {
//            showColAiScore ? col.estensaDetails.primary = true : col.estensaDetails.primary = false;
//        }
//    });
//}
